import { Alert, Box, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { FeedbackSnackbar, ResourcePage, ResourceCollection, StatusBadge, ValueBadge, type ResourceCollectionState } from '../../../design-system';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, CatalogDetailDialog, useReferenceLifecycle } from './ReferenceLifecycle';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  LocationEditorDialog,
  emptyLocationDraft,
  locationDraftValid,
  type LocationDraft,
  type LocationEditor,
} from './LocationEditorDialog';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  locationTypeLabel,
  replaceCatalogItem,
  replaceCatalogItems,
  sortCatalog,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  type ReferenceSettingsCommonProps,
  type ReferenceDataResource,
} from '../model/referenceData';
import { referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type {
  ReferenceLocation,
  ReferenceLocationChangeInput,
  ReferenceLocationCreateInput,
  ReferenceLocationType,
} from '../model/types';
export function LocationReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  resource,
}: ReferenceSettingsCommonProps & {
  resource: ReferenceDataResource<ReferenceLocation>;
}) {
  const [items, setItems] = useState<ReferenceLocation[]>(resource.data ?? []);
  const [selected, setSelected] = useState<ReferenceLocation>();
  const [editor, setEditor] = useState<LocationEditor>();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.locations(), []);
  useEffect(() => setItems(sortCatalog(resource.data ?? [])), [resource.data]);
  const filteredItems = useMemo(() => filterCatalog(items, query, status), [items, query, status]);
  const columns = useMemo<GridColDef<ReferenceLocation>[]>(
    () => [
      {
        field: 'descriptor',
        headerName: '근무지',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 150,
      },
      {
        field: 'locationType',
        headerName: '유형',
        width: 120,
        valueFormatter: (value: ReferenceLocationType) => locationTypeLabel(value),
        renderCell: ({ row }) => <ValueBadge label={locationTypeLabel(row.locationType)} />,
      },
      {
        field: 'countryCode',
        headerName: '국가',
        width: 80,
      },
      {
        field: 'timeZone',
        headerName: '시간대',
        width: 150,
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [],
  );
  const updateItem = (item: ReferenceLocation) => {
    setItems((current) => replaceCatalogItem(current, item));
    setSelected((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) resource.reload();
  };
  const lifecycle = useReferenceLifecycle<ReferenceLocation>({
    kindLabel: '근무지',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceLocation(id, input as ReferenceLocationChangeInput, key),
    fixtureBlockers: {
      'location-seoul': [
        {
          type: 'WORKER_JOB',
          count: 18,
        },
        {
          type: 'SUPERVISORY_ORGANIZATION',
          count: 4,
        },
      ],
    },
    onChanged: updateItem,
    onNotice: setNotice,
  });
  const openCreate = () => {
    setEditor({
      mode: 'create',
      draft: emptyLocationDraft(),
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openEdit = (item: ReferenceLocation) => {
    setSelected(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        locationType: item.locationType,
        countryCode: item.countryCode,
        timeZone: item.timeZone,
        effectiveDate: todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeDraft = <K extends keyof LocationDraft>(field: K, value: LocationDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !locationDraftValid(editor.draft, editor.mode) || busy) return;
    const draft = editor.draft;
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceLocation>;
    if (editor.mode === 'create') {
      const payload: ReferenceLocationCreateInput = {
        descriptor: draft.descriptor.trim(),
        locationType: draft.locationType,
        countryCode: draft.countryCode.trim().toUpperCase(),
        timeZone: draft.timeZone.trim(),
        effectiveDate: draft.effectiveDate,
        reason: draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `location-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            locationType: payload.locationType,
            countryCode: payload.countryCode,
            timeZone: payload.timeZone,
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceLocation(payload, key);
    } else {
      const payload: ReferenceLocationChangeInput = {
        version: editor.item!.version,
        descriptor: draft.descriptor.trim(),
        locationType: draft.locationType,
        countryCode: draft.countryCode.trim().toUpperCase(),
        timeZone: draft.timeZone.trim(),
        effectiveDate: draft.effectiveDate,
        reason: draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            locationType: payload.locationType ?? editor.item!.locationType,
            countryCode: payload.countryCode ?? editor.item!.countryCode,
            timeZone: payload.timeZone ?? editor.item!.timeZone,
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceLocation(editor.item!.id, payload, key);
    }
    void request
      .then(
        (saved) => {
          updateItem(saved);
          setEditor(undefined);
          setNotice(
            editor.mode === 'create'
              ? `${saved.descriptor} 근무지를 등록했습니다.`
              : `${saved.descriptor} 근무지 정보를 변경했습니다.`,
          );
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '근무지 정보를 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const collectionState: ResourceCollectionState = resource.loading
    ? {
        kind: 'loading',
        label: '근무지 목록을 불러오는 중입니다.',
      }
    : resource.error
      ? {
          kind: 'error',
          message: resource.error,
          onRetry: resource.reload,
          retryLabel: '근무지 목록 다시 시도',
        }
      : items.length === 0
        ? {
            kind: 'empty',
            title: '등록된 근무지가 없습니다.',
            description: canWrite ? '근무지 등록을 눌러 첫 근무지를 추가해 주세요.' : '등록된 근무지가 없습니다.',
          }
        : {
            kind: 'ready',
          };
  return (
    <>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      <ResourcePage
        title="근무지"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "근무지 등록",
          onCreate: openCreate,
          onBulkCreate: () => setBulkOpen(true),
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          {!canRead ? (
            <Stack
              spacing={1.5}
              sx={{
                mb: canRead ? 1.5 : 0,
                flexShrink: 0,
              }}
            >
              {!canRead ? (
                <Alert severity="warning">근무지 기준정보를 조회하려면 staffing.read 권한이 필요합니다.</Alert>
              ) : null}
            </Stack>
          ) : null}
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={collectionState}
                rows={filteredItems}
                totalCount={items.length}
                columns={[...columns, catalogActionsColumn({ canWrite, onInspect: setSelected, onEdit: openEdit, onLifecycle: lifecycle.open })]}
                query={query}
                onQueryChange={setQuery}
                searchPlaceholder="근무지 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '상태',
                    value: status,
                    onChange: setStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openEdit(row) : setSelected(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <CatalogDetailDialog
        item={selected}
        kindLabel="근무지"
        canWrite={canWrite}
        canEdit={selected?.status !== 'SCHEDULED'}
        editDisabledReason={
          selected?.status === 'SCHEDULED' ? '사용 예정인 근무지 속성은 적용일 이후 수정할 수 있습니다.' : undefined
        }
        extraItems={
          selected
            ? [
                ['유형', <ValueBadge label={locationTypeLabel(selected.locationType)} />],
                ['국가', selected.countryCode],
                ['시간대', selected.timeZone],
              ]
            : []
        }
        onClose={() => setSelected(undefined)}
        onEdit={() => selected && openEdit(selected)}
        onLifecycle={(action) => selected && lifecycle.open(action, selected)}
      />

      <LocationEditorDialog
        editor={editor}
        busy={busy}
        editorError={editorError}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        onChange={changeDraft}
      />
      <ReferenceBulkCreateDialog<ReferenceLocation>
        open={bulkOpen}
        workflow={bulkWorkflow}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.location(row);
          return fixtureMode
            ? Promise.resolve({
                id: `location-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                locationType: payload.locationType,
                countryCode: payload.countryCode,
                timeZone: payload.timeZone,
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceLocation(payload, key);
        }}
        onCreated={(created) => {
          setItems((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) resource.reload();
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `근무지 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `근무지 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {lifecycle.dialog}
    </>
  );
}
